import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../common/style/Theme";
import Typewriter from "../../components/Typewriter";
import { ArrowForward, AspectRatio, Moving, Psychology, Transform, Web } from "@mui/icons-material";
import { useState } from "react";
import { default as project } from "../../assets/project.png";
import WaveCard from "../../components/WaveCard";
import { default as agriculture } from "../../assets/agri.jpg";
import { default as distribution } from "../../assets/supply.jpg";
import { default as ecommerce } from "../../assets/ecommerce.jpg";
import { default as service } from "../../assets/service.jpg";
import { default as rural } from "../../assets/rural.jpg";
import { default as manufacturing } from "../../assets/manufacturing.jpg";
import { default as development } from "../../assets/development.png";
import { default as bg } from "../../assets/bg.jpg";
import { default as platform } from "../../assets/platform.jpg";
import { default as bhoomee } from "../../assets/bhoomee.png";
import { useNavigate } from "react-router-dom";

const Home = () => {
    const navigate = useNavigate();
    // Media queries for responsive design
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

    const [index, setIndex] = useState(0)

    const tabItems = [
        {
            icon: <Moving style={{
                fontSize: isMobile ? "2rem" : "3rem",
                color: theme.palette.secondary.main
            }}/>, 
            title: "Retain and grow customers",
            subtitle: "Build lasting relationships that drive revenue",
            description: "Transform one-time buyers into loyal advocates through personalized experiences, data-driven insights, and strategic engagement platforms. Our customer retention solutions help you understand behavior patterns, predict needs, and deliver value that keeps customers coming back while increasing their lifetime value."
        },
        {
            icon: <AspectRatio style={{
                fontSize: isMobile ? "2rem" : "3rem",
                color: theme.palette.secondary.main
            }}
            />, 
            title: "Create operational scalability",
            subtitle: "Systems that grow with your ambitions",
            description: "Design flexible infrastructure and automated processes that support exponential growth without proportional increases in overhead. We build scalable architectures, implement efficient workflows, and create systems that maintain performance and quality as your business expands from startup to enterprise level."
        },
        {
            icon: <Web style={{
                fontSize: isMobile ? "2rem" : "3rem",
                color: theme.palette.secondary.main
            }}
            />, 
            title: "Streamline business process",
            subtitle: "Eliminate friction, maximize efficiency",
            description: "Identify bottlenecks, automate repetitive tasks, and optimize workflows to reduce costs and improve productivity. Our process optimization solutions map your current operations, design efficient alternatives, and implement digital tools that make your team more effective and your business more agile."
        },
        {
            icon: <Psychology style={{
                fontSize: isMobile ? "2rem" : "3rem",
                color: theme.palette.secondary.main
            }}
            />, 
            title: "Become an innovator",
            subtitle: "Lead your industry with breakthrough solutions",
            description: "Leverage cutting-edge technologies like AI, IoT, and machine learning to create competitive advantages and new revenue streams. We help you identify innovation opportunities, prototype solutions, and implement technologies that position your business as an industry leader and market disruptor."
        },
        {
            icon: <Transform style={{
                fontSize: isMobile ? "2rem" : "3rem",
                color: theme.palette.secondary.main
            }}/>, 
            title: "Digital transformation",
            subtitle: "Modernize for the digital-first world",
            description: "Transition from traditional operations to digital-native processes that improve customer experience, operational efficiency, and business agility. Our comprehensive transformation approach covers technology infrastructure, process redesign, team training, and cultural change management to ensure successful digital evolution."
        },
    ]

    const offeringDetails = [
        {
            title: "Mobile Apps for All Industries",
            description: "Mobile-first experiences that drive engagement and productivity.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/mobile"
        },
        {
            title: "eCommerce & Marketplace Integration",
            description: "Build digital commerce platforms that convert.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/ecommerce"
        },
        {
            title: "UI/UX Design & Prototype Building",
            description: "Design-led solutions that people love to use.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/ui-ux"
        },
        {
            title: "Third-party Integrations",
            description: "Make your systems talk. Build an ecosystem.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/integrations"
        },
        {
            title: "Full-stack Development",
            description: "Build powerful digital solutions for any industry.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/full-stack"
        },
        {
            title: "Business Process Mapping",
            description: "Reimagine how your business works.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/bpm"
        },
        {
            title: "⁠Workflow Automation & Dashboards",
            description: "Simplify processes. Track performance. Make data-driven decisions.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/workflow"
        },
        {
            title: "Custom ERP & SaaS Development",
            description: "Build custom SaaS. Streamline operations. Scale your business.",
            icon: <Psychology style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/erp"
        },
        {
            title: "⁠AI-enabled Tools & Smart Systems",
            description: "Unlock intelligence within your operations.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/ai-tools"
        },
        {
            title: "Data Intelligence & Reporting Modules",
            description: "Turn your data into strategic advantage.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/data-intelligence"
        },
        {
            title: "Digital Marketing & SEO",
            description: "Reach the right audience, in the right way.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/digital-marketing"
        },
        {
            title: "Tech Partnership Models",
            description: "Your long-term tech partner, not just a vendor.",
            icon: <Web style={{ fontSize: "1.3rem", color: theme.palette.common.white }} />,
            link: "/offerings/partnerships"
        },
    ]

    const industries = [
        {
            title: "Agriculture & Allied Sectors",
            description: "Rooted in agri-tech innovation, we power platforms like Bhoomee to connect farmers, input providers, buyers, and rural entrepreneurs. We also support FPOs, agri-retailers, agri-fintechs, and ecosystem partners.",
            image: agriculture,
            link: "/industries/agriculture"
        },
        {
            title: "Distribution & Supply Chain",
            description: "We enable real-time visibility and end-to-end coordination across multi-level supply chains — from regional hubs to last-mile delivery. Ideal for fast-moving goods, retail networks, and rural distribution models.",
            image: distribution,
            link: "/industries/distribution"
        },
        {
            title: "⁠Retail & eCommerce",
            description: "We support both niche D2C brands and multi-vendor marketplaces with customized eCommerce platforms, order management systems, digital wallets, and CRM integrations — across mobile and web.",
            image: ecommerce,
            link: "/industries/retail"
        },
        {
            title: "Service-based Enterprises",
            description: "From consulting firms and logistics providers to healthcare and education startups, we help digitize service delivery models through portals, booking engines, CRM systems, and performance dashboards.",
            image: service,
            link: "/industries/service-based"
        },
        {
            title: "Rural & Social Enterprises",
            description: "We work closely with NGOs, CSR initiatives, and social startups to build platforms that create access, inclusion, and opportunity. From digital skilling platforms to rural commerce apps — we turn missions into scalable solutions.",
            image: rural,
            link: "/industries/rural"
        },
        {
            title: "Manufacturing & Processing",
            description: "From factory floor to finished goods, we design ERP systems, workflow automation, inventory control, and quality management solutions for manufacturers of all sizes. Our tools streamline operations, ensure traceability, and improve productivity across units.",
            image: manufacturing,
            link: "/industries/manufacturing"
        },
        {
            title: "Public Sector Programs",
            description: "We support public-private partnerships and government-aligned programs with customized platforms for data collection, stakeholder mapping, beneficiary engagement, and outcome monitoring.",
            image: development,
            link: "/industries/public-sector"
        }
    ]

    return (
        <Box style={{
            overflowX: "hidden",
            width: "100%",
        }}>
            <Box style={{
                width: "100vw",
                height: isMobile ? "100vh" : "100vh",
                minHeight: isMobile ? "600px" : "100vh",
                position: "relative",
            }}>
                <img src={bg} alt="background" style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: isMobile ? "center" : "right",
                }}/>
                <Grid container style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}>
                    <Grid size={{xs: 12, sm: 12, md: 8, lg: 8}} style={{
                        paddingTop: isTablet && window.innerWidth < 990 ? "55vh" : "10vw",
                        paddingLeft: "5vw",
                        paddingRight: "5vw"
                    }}>
                        <Typewriter />
                        <Typography variant="body1" style={{
                            color: theme.palette.common.white,
                            marginTop: "20px",
                            paddingRight: "12vw"
                        }}>
                            At M5, we empower businesses to transform into agile, tech-enabled enterprises by aligning strategy, innovation, and digital execution. We don’t just deliver solutions — we build future-ready organizations.
                        </Typography>
                        {/* Modern Flagship Product Design */}
                        <Box style={{
                            marginTop: "60px",
                            display: "flex",
                            alignItems: "center",
                            gap: "30px",
                            cursor: "pointer",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            padding: "20px 0",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateX(15px)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateX(0)";
                        }}
                        onClick={() => {
                            navigate("/platforms")
                            window.scroll({
                                top: 0,
                                behavior: "smooth"
                            })
                        }}>
                            {/* Animated line indicator */}
                            <Box style={{
                                width: "4px",
                                height: "80px",
                                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                borderRadius: "2px",
                                position: "relative",
                                overflow: "hidden"
                            }}>
                                <Box style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "30px",
                                    background: `linear-gradient(180deg, ${theme.palette.common.white}80, transparent)`,
                                    animation: "slide-down 2s ease-in-out infinite",
                                    borderRadius: "2px"
                                }} />
                            </Box>
                            
                            {/* Content Section */}
                            <Box style={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1
                            }}>
                                <Typography variant="caption" style={{
                                    color: theme.palette.common.white + "cc",
                                    fontWeight: 600,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                    fontSize: "11px",
                                    marginBottom: "8px"
                                }}>
                                    Our Flagship Product
                                </Typography>
                                
                                <Box style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "20px"
                                }}>
                                    {/* Bhoomee Logo with Glow Effect */}
                                    <Box style={{
                                        position: "relative",
                                        width: "160px",
                                        height: "60px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}>
                                        {/* Glow background */}
                                        <Box style={{
                                            position: "absolute",
                                            width: "180px",
                                            height: "80px",
                                            background: `radial-gradient(ellipse, ${theme.palette.secondary.main}30, transparent 60%)`,
                                            filter: "blur(10px)",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            animation: "pulse-glow 3s ease-in-out infinite"
                                        }} />
                                        
                                        <img src={bhoomee} alt="Bhoomee Platform" style={{
                                            width: "160px",
                                            height: "60px",
                                            objectFit: "contain",
                                            objectPosition: "center",
                                            filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
                                            position: "relative",
                                            zIndex: 1
                                        }}/>
                                    </Box>
                                    
                                    {/* Arrow and Text */}
                                    <Box style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "15px",
                                        opacity: 0.9
                                    }}>
                                        <Box style={{
                                            width: "40px",
                                            height: "2px",
                                            background: `linear-gradient(90deg, ${theme.palette.common.white}, transparent)`,
                                            position: "relative"
                                        }}>
                                            <Box style={{
                                                position: "absolute",
                                                right: "-6px",
                                                top: "-4px",
                                                width: "10px",
                                                height: "10px",
                                                borderTop: `2px solid ${theme.palette.common.white}`,
                                                borderRight: `2px solid ${theme.palette.common.white}`,
                                                transform: "rotate(45deg)"
                                            }} />
                                        </Box>
                                        
                                        <Typography variant="body2" style={{
                                            color: theme.palette.common.white,
                                            fontWeight: 500,
                                            fontSize: "14px"
                                        }}>
                                            Explore Platform
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Typography variant="caption" style={{
                                    color: theme.palette.common.white + "99",
                                    fontSize: "12px",
                                    marginTop: "8px",
                                    fontStyle: "italic"
                                }}>
                                    Connecting farmers with digital innovation
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{xs: 12, sm: 12, md: 4, lg: 4}}>
                    </Grid>
                </Grid>
            </Box>
            <Grid container style={{
                padding: isMobile ? "8vw 5vw" : "5vw 0"
            }}>
                <Typography variant="h2" style={{
                    textAlign: "center",
                    width: "100%",
                }}>
                    We Help Our Clients
                </Typography>
                <Typography variant="body1" style={{
                    textAlign: "center",
                    width: "100%",
                    marginBottom: isMobile ? "30px" : "50px",
                    padding: isMobile ? "0 5vw" : "0",
                }}>
                    We help our clients achieve their business goals by providing them with the best possible solutions.
                </Typography>
                <Box style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "stretch",
                    background: theme.palette.background.paper,
                    flexDirection: isMobile ? "column" : "row",
                    overflow: isMobile ? "visible" : "hidden",
                }}>
                    {tabItems.map((item, i) => (
                        <Box key={i} style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: isMobile ? "4vw 8vw" : "1vw",
                            width: isMobile ? "90%" : "20%",
                            borderRight: isMobile ? "none" : `0.3px solid ${theme.palette.background.default}`,
                            borderBottom: isMobile ? 
                                (i === index ? "none" : `0.3px solid ${theme.palette.background.default}`) :
                                (i === index ? "none" : `0.3px solid ${theme.palette.background.default}`),
                            borderTop: i === index ?
                                `2px solid ${theme.palette.secondary.main}` :
                                `0.3px solid ${theme.palette.background.default}`,
                            background: theme.palette.common.white,
                            cursor: "pointer",
                        }} onClick={() => setIndex(i)}>
                            {item.icon}
                            <Typography variant="body2" style={{
                                color: theme.palette.common.black,
                                textAlign: "center",
                                marginTop: "10px"
                            }}>
                                {item.title}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Grid container style={{
                    padding: isMobile ? "8vw 5vw" : "4vw 10vw",
                    flexDirection: isMobile ? "column-reverse" : "row",
                    alignItems: "center"
                }}>
                    <Grid size={{xs: 12, sm: 12, md: 4, lg: 4}} style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                        <Box style={{
                            width: isMobile ? "250px" : "280px",
                            height: isMobile ? "250px" : "280px",
                            background: theme.palette.primary.main,
                            borderRadius: "20px",
                            position: "relative",
                        }}>
                            <Box style={{
                                width: isMobile ? "250px" : "280px",
                                height: isMobile ? "250px" : "280px",
                                transform: "rotate(8deg)",
                                position: "absolute",
                                background: theme.palette.warning.main,
                                top: 0,
                                left: 0,
                                borderRadius: "20px"
                            }}></Box>
                            <Box
                                style={{
                                    width: isMobile ? "300px" : "400px",
                                    height: isMobile ? "300px" : "400px",
                                    position: "absolute",
                                    top: isMobile ? "-25px" : "-50px",
                                    left: isMobile ? "-25px" : 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    zIndex: 1,
                                }}
                            >
                                <img src={project} alt="project" style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    objectPosition: "center",
                                    borderRadius: "20px",
                                }}/>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{xs: 12, sm: 12, md: 8, lg: 8}} style={{
                        paddingLeft: isMobile || isTablet ? "0" : "100px",
                        paddingTop: isMobile || isTablet ? "5vw" : "0",
                        paddingRight: isMobile ? "0" : "0",
                        marginBottom: isMobile ? "40px" : "0",
                    }}>
                        <Typography variant="h3" style={{
                            marginTop: "10px",
                            textAlign: isMobile || isTablet? "center" : "left",
                        }}>
                            {tabItems[index].subtitle}
                        </Typography>
                        <Typography variant="body1" style={{
                            marginTop: "20px",
                            textAlign: isMobile || isTablet ? "center" : "left",
                        }}>
                            {tabItems[index].description}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container style={{
                background: `linear-gradient(35deg,${theme.palette.common.black},${theme.palette.primary.main})`,
                padding: isMobile ? "8vw 5vw" : "5vw 10vw",
            }} spacing={2}>
                <Typography variant="h2" style={{
                    textAlign: "center",
                    width: "100%",
                    marginBottom: isMobile ? "8vw" : "5vw",
                    color: theme.palette.common.white,
                }}>
                    Our Offerings
                </Typography>
                {offeringDetails.map((offering, idx) => (
                    <Grid key={idx} size={{xs:12, sm:6, md:4, lg:3}} style={{paddingBottom: "20px"}}>
                        <Box style={{
                            background: `linear-gradient(215deg, ${theme.palette.background.default}, ${theme.palette.secondary.main})`,
                            padding: "4px",
                            borderRadius: "8px",
                            height: "100%",
                        }}>
                            <Box style={{
                                width: "inherit",
                                height: "calc(100% - 3vw)",
                                background: theme.palette.common.white,
                                borderRadius: "8px",
                                padding: isMobile ? "4vw" : "1.5vw",
                                cursor: "pointer",
                            }} onClick={() => {
                                navigate(offering.link)
                                window.scroll({
                                    top: 0,
                                    behavior: "smooth"
                                })
                            }}>
                                <Box style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "40px",
                                    height: "40px",
                                    background: theme.palette.secondary.main,
                                    borderRadius: "50%",
                                }}>
                                    {offering.icon}
                                </Box>
                                <Typography variant="h6" style={{
                                    marginTop: "30px",
                                }}>
                                    {offering.title}
                                </Typography>
                                <Typography variant="body1" style={{
                                    marginTop: "10px",
                                }}>
                                    {offering.description}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
            <Grid container style={{
                padding: isMobile ? "8vw 5vw" : "5vw 8vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }} spacing={2}>
                <Typography variant="h2" style={{
                    textAlign: "center",
                    width: "100%",
                    marginBottom: isMobile ? "6vw" : "3vw",
                }}>
                    Industries
                </Typography>
                {industries.map((industry, index) => (
                    <Grid key={index} size={{xs:12, sm:6, md:4, lg:3}} style={{paddingBottom: "20px"}}>
                        <WaveCard {...industry} index={index} onClick={() => {
                            navigate(industry.link)
                            window.scroll({
                                top: 0,
                                behavior: "smooth"
                            })
                        }}/>
                    </Grid>
                ))}
            </Grid>
            <Grid container style={{
                background: theme.palette.primary.main,
                color: theme.palette.common.white,
                padding: isMobile ? "8vw 5vw" : "5vw 10vw",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: isMobile ? "column" : "row",
            }}>
                <Grid size={{ xs: 12, sm: 12, md: 9, lg: 9 }} style={{
                    paddingRight: isMobile ? "0" : "10vw",
                    marginBottom: isMobile ? "30px" : "0",
                    textAlign: isMobile ? "center" : "left",
                }}>
                    <Typography variant="h2" style={{
                        fontWeight: 600,
                        marginBottom: "20px",
                        lineHeight: "1.2",
                        color: theme.palette.common.white
                    }}>
                        Ready to take your business to the next level?
                    </Typography>
                    <Typography variant="body1" style={{
                        marginBottom: isMobile ? "0" : "30px",
                    }}>
                        Whether you need assistance with web development, mobile app development, digital marketing, or any other service, our team is ready to assist you. Let's work together to create a successful future for your business.
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }} style={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "flex-end",
                }}>
                    <Button variant="contained" endIcon={<ArrowForward />} style={{
                        width: isMobile ? "80%" : "100%",
                    }} onClick={() => {
                        navigate("/about")
                        window.scroll({
                            top: 0,
                            behavior: "smooth"
                        })
                    }}>
                        Get in touch with us
                    </Button>
                </Grid>
            </Grid>
            <Grid container style={{
                padding: isMobile ? "8vw 5vw" : "5vw 10vw",
                display: "flex",
                alignItems: "stretch",
                flexDirection: isMobile ? "column" : "row",
            }}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} style={{
                    marginBottom: isMobile ? "0" : "0",
                    background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.light}37)`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                    <img src={platform} alt="healthcare" style={{
                        width: "90%",
                        height: isMobile ? "250px" : "350px",
                        objectFit: "contain",
                        objectPosition: "center",
                        borderRadius: "10px",
                        border: `2px solid ${theme.palette.primary.light}`,
                        background: theme.palette.common.white,
                    }}/>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} style={{
                    background: `${theme.palette.primary.light}37`,
                    padding: isMobile ? "30px 20px" : "40px"
                }}>
                    <Box style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        marginBottom: isMobile ? "4vw" : "2vw",
                        flexDirection: isMobile ? "column" : "row",
                        textAlign: isMobile ? "center" : "left",
                    }}>
                        <Box style={{
                            width: "150px",
                            height: "50px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "50%",
                            marginBottom: isMobile ? "15px" : "0",
                        }}>
                            <img src={bhoomee} alt="bhoomee" style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                objectPosition: "center",
                            }}/>
                        </Box> 
                    </Box>
                    <Box style={{
                        display: "flex",
                        justifyContent: isMobile ? "center" : "flex-start",
                        alignItems: "center",
                        marginBottom: "10px",
                        paddingRight: isMobile ? "0" : "5vw",
                        flexWrap: "wrap"
                    }}>
                        <Typography variant="body2">Digital Farmer Services</Typography>
                        <Box style={{
                            width: "5px",
                            height: "5px",
                            background: theme.palette.background.default,
                            borderRadius: "50%",
                            margin: "0 10px",
                        }}/>
                        <Typography variant="body2">On Ground Services</Typography>
                        <Box style={{
                            width: "5px",
                            height: "5px",
                            background: theme.palette.background.default,
                            borderRadius: "50%",
                            margin: "0 10px",
                        }}/>
                        <Typography variant="body2">Farmerprenuer</Typography>
                        <Box style={{
                            width: "5px",
                            height: "5px",
                            background: theme.palette.background.default,
                            borderRadius: "50%",
                            margin: "0 10px",
                        }}/>
                        <Typography variant="body2">Rural eCommerce</Typography>
                    </Box>
                    <Typography variant="h3" style={{ 
                        lineHeight: "1.2", 
                        fontWeight: 600,
                        textAlign: isMobile ? "center" : "left",
                    }}>
                        Bhoomee, an innovative initiative by M5 Info Solutions Private Limited.
                    </Typography>
                    <Typography variant="body1" style={{
                        marginTop: "20px",
                        textAlign: isMobile ? "center" : "left",
                    }}>
                        This first-of-its-kind platform leverages advanced cloud, mobile, and digital technologies, combined with a unique network of local Udyamis, to provide last-mile connectivity and equal opportunities for all farmers, regardless of smartphone access.
                    </Typography>
                    <Box style={{
                        display: "flex",
                        justifyContent: isMobile ? "center" : "flex-end",
                        marginTop: "30px",
                    }}>
                        <Button variant="contained" style={{
                            background: theme.palette.primary.main,
                            color: theme.palette.common.white,
                            marginTop: "20px",
                        }} endIcon={<ArrowForward />} onClick={() => {
                            window.open("https://bhoomee.co.in", "_blank")
                        }}>
                            Visit Site
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Home;